import { useState, useEffect, useRef } from 'react';

interface Position {
  right: number;
  bottom: number;
}

interface UseDraggableOptions {
  initialPosition: Position;
  handleRef: React.RefObject<HTMLElement | null>;
  elementRef: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
}

interface UseDraggableReturn {
  position: Position;
  setPosition: (pos: Position) => void;
  isDragging: boolean;
}

export function useDraggable({ initialPosition, handleRef, elementRef, enabled = true }: UseDraggableOptions): UseDraggableReturn {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);

  // Use refs for drag state to avoid stale closures and effect churn
  const positionRef = useRef<Position>(initialPosition);
  const draggingRef = useRef(false);
  const startPointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const startPosRef = useRef<Position>({ right: 0, bottom: 0 });

  // Keep positionRef in sync with state
  positionRef.current = position;

  useEffect(() => {
    const handle = handleRef.current;
    if (handle === null) return;

    const onPointerDown = (e: PointerEvent) => {
      startPosRef.current = { ...positionRef.current };
      startPointerRef.current = { x: e.clientX, y: e.clientY };
      draggingRef.current = true;
      setIsDragging(true);
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!draggingRef.current || elementRef.current === null) return;

      const deltaX = e.clientX - startPointerRef.current.x;
      const deltaY = e.clientY - startPointerRef.current.y;

      let newRight = startPosRef.current.right - deltaX;
      let newBottom = startPosRef.current.bottom - deltaY;

      // Clamp to viewport
      const rect = elementRef.current.getBoundingClientRect();
      const maxRight = window.innerWidth - rect.width;
      const maxBottom = window.innerHeight - rect.height;

      newRight = Math.max(0, Math.min(newRight, maxRight));
      newBottom = Math.max(0, Math.min(newBottom, maxBottom));

      const newPos = { right: newRight, bottom: newBottom };
      positionRef.current = newPos;
      setPosition(newPos);
    };

    const onPointerUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
    };

    handle.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);

    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [handleRef, elementRef, enabled]);

  return { position, setPosition, isDragging };
}
